import { Play } from "lucide-react";
import { FadeUp, SectionTag } from "./Reveal";

const SLICE_IMG =
    "https://images.unsplash.com/photo-1652058812572-fad90a1ee06b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxuZXclMjB5b3JrJTIwY2l0eSUyMHNreWxpbmUlMjBkYXJrJTIwbW9vZHklMjBhdG1vc3BoZXJpYyUyMG1pc3QlMjBuaWdodCUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc4OTczNDkxOXww&ixlib=rb-4.1.0&q=85";
const LOFT_IMG =
    "https://images.pexels.com/photos/36353346/pexels-photo-36353346.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PROMISES = [
    "Walk the first district of the city",
    "Meet other players in shared spaces",
    "Customize your character and style",
    "Step inside your first personal space",
];

export const VerticalSlice = () => (
    <section id="slice" className="relative py-24 sm:py-32 bg-[#0D0D12]/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <SectionTag index="04" label="The Vertical Slice" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                <div className="lg:col-span-5">
                    <FadeUp>
                        <div
                            data-testid="slice-status-pill"
                            className="inline-flex items-center gap-3 border border-[#A855F7]/40 bg-[#A855F7]/10 px-4 py-2 mb-8"
                        >
                            <span className="w-2 h-2 rounded-full bg-[#A855F7] animate-pulse-dot" />
                            <span className="font-mono-meta text-[10px] tracking-[0.3em] uppercase text-[#C084FC]">
                                In active development
                            </span>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.05}>
                        <h2
                            data-testid="slice-heading"
                            className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white leading-tight"
                        >
                            One slice of the city,{" "}
                            <span className="font-serif-accent italic font-medium text-[#C084FC]">
                                built to prove the whole.
                            </span>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.12}>
                        <p className="mt-6 text-base text-slate-300 leading-relaxed">
                            We are currently developing a playable vertical slice — a focused,
                            polished demonstration of the core experience and the vision behind
                            it. Not the whole city. Just enough of it to make you want to live
                            there.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.18}>
                        <ul className="mt-8 space-y-4">
                            {PROMISES.map((p, i) => (
                                <li
                                    key={p}
                                    data-testid={`slice-promise-${i + 1}`}
                                    className="flex items-center gap-4 text-sm sm:text-base text-slate-300"
                                >
                                    <span className="font-mono-meta text-[10px] tracking-[0.2em] text-[#A855F7]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="h-px w-6 bg-[#A855F7]/50" />
                                    {p}
                                </li>
                            ))}
                        </ul>
                    </FadeUp>
                    <FadeUp delay={0.24}>
                        <p className="mt-10 font-mono-meta text-[10px] tracking-[0.25em] uppercase text-slate-500 leading-loose">
                            Screenshots, gameplay footage, and dev updates
                            <br />
                            will live here as the slice takes shape.
                        </p>
                    </FadeUp>
                </div>

                <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <FadeUp delay={0.1}>
                        <figure className="group" data-testid="slice-frame-1">
                            <div className="overflow-hidden border border-white/10">
                                <img
                                    src={SLICE_IMG}
                                    alt="Atmospheric city street concept"
                                    loading="lazy"
                                    className="w-full aspect-[3/4] object-cover img-treatment group-hover:scale-105 transition-transform duration-[1400ms] ease-out"
                                />
                            </div>
                            <figcaption className="mt-3 font-mono-meta text-[10px] tracking-[0.25em] uppercase text-slate-500">
                                District mood — stand-in imagery
                            </figcaption>
                        </figure>
                    </FadeUp>
                    <FadeUp delay={0.2} className="sm:mt-14">
                        <figure className="group" data-testid="slice-frame-2">
                            <div className="overflow-hidden border border-white/10">
                                <img
                                    src={LOFT_IMG}
                                    alt="Personal apartment interior concept"
                                    loading="lazy"
                                    className="w-full aspect-[3/4] object-cover img-treatment group-hover:scale-105 transition-transform duration-[1400ms] ease-out"
                                />
                            </div>
                            <figcaption className="mt-3 font-mono-meta text-[10px] tracking-[0.25em] uppercase text-slate-500">
                                Personal space mood — stand-in imagery
                            </figcaption>
                        </figure>
                    </FadeUp>
                    <FadeUp delay={0.28} className="sm:col-span-2">
                        <div
                            data-testid="slice-footage-placeholder"
                            className="group relative border border-dashed border-white/20 hover:border-[#A855F7]/50 transition-colors duration-500 aspect-video flex flex-col items-center justify-center gap-4 bg-black/30"
                        >
                            <span className="w-14 h-14 rounded-full border border-white/25 group-hover:border-[#A855F7] flex items-center justify-center transition-colors duration-500">
                                <Play className="w-5 h-5 text-white/60 group-hover:text-[#A855F7] transition-colors duration-500 ml-0.5" />
                            </span>
                            <span className="font-mono-meta text-[10px] tracking-[0.35em] uppercase text-slate-500">
                                Gameplay footage — coming soon
                            </span>
                        </div>
                    </FadeUp>
                </div>
            </div>
        </div>
    </section>
);
